// --- START PATCH: Add public /login & /signup routes with PublicAuthRoute, lazy auth pages; keep grouped routes; remove legacy /Home --- 
import { Suspense, lazy } from "react";
import { Route, Routes, Navigate } from "react-router-dom";

import Navbar from "@/features/layout/Navbar";
import Footer from "@/features/layout/Footer";
import { ThemeProvider } from "./theme/ThemeContext";
import "./styles/global.css";
import { Toaster } from "sonner";

import UploadModel from "@/pages/shop/UploadModel";
import Portfolio from "@/pages/marketing/Portfolio";
import Contact from "@/pages/marketing/Contact";
import NotFound from "@/pages/NotFound";
import ProtectedRoute from "@/auth/ProtectedRoute";
import PublicAuthRoute from "@/auth/PublicAuthRoute";
import AdminRoute from "@/auth/AdminRoute";
import CartPage from "@/pages/shop/Cart";
import CheckoutPage from "@/pages/shop/Checkout";
import MaterialGuide from "@/pages/marketing/MaterialGuide";
import Profile from "@/pages/account/Profile";
import ProductsPage from "@/pages/shop/Products";
import ProductDetailPage from "@/pages/shop/ProductDetail";
import FilamentsPage from "@/pages/shop/Filaments";
import FilamentDetailPage from "@/pages/shop/FilamentDetail";

// Layouts
import AccountLayout from "@/layouts/AccountLayout";
import AdminLayout from "@/layouts/AdminLayout";

// Lazy pages (account)
const OrdersPage = lazy(() => import("@/pages/account/Orders"));
const OrderDetailPage = lazy(() => import("@/pages/account/Orders/OrderDetail"));
const CompanyPage = lazy(() => import("@/pages/account/Company/index"));
const SettingsPage = lazy(() => import("@/pages/account/Settings/index"));

// Lazy auth
const LoginPage = lazy(() => import("@/pages/auth/Login"));
const SignUpPage = lazy(() => import("@/pages/auth/SignUp"));

// Lazy admin
const AdminClients = lazy(() => import("@/pages/admin/Clients"));
const InventoryDashboard = lazy(() => import("@/pages/admin/Inventory"));
const StoreSettings = lazy(() => import("@/pages/admin/StoreSettings"));
const ShippingMethods = lazy(() => import("@/pages/admin/ShippingMethods"));
const CompanyPayments = lazy(() => import("@/pages/admin/CompanyPayments"));
const AdminOrders = lazy(() => import("@/pages/admin/Orders"));
const AdminOrderDetail = lazy(() => import("@/pages/admin/Orders/AdminOrderDetail"));

// Lazy admin catalog
const AdminCatalogProducts = lazy(() => import("@/pages/admin/Catalog/Products"));
const AdminCatalogFilaments = lazy(() => import("@/pages/admin/Catalog/Filaments"));
const AdminCatalogCategories = lazy(() => import("@/pages/admin/Catalog/Categories"));

// Lazy legal pages
const PrivacyPage = lazy(() => import("@/pages/legal/Privacy"));
const TermsPage = lazy(() => import("@/pages/legal/Terms"));
const CookiesPage = lazy(() => import("@/pages/legal/Cookies"));

function RouteFallback() {
  return (
    <div className="flex items-center justify-center py-24">
      <div className="space-y-2">
        <div className="h-4 w-40 bg-muted animate-pulse rounded" />
        <div className="h-4 w-60 bg-muted animate-pulse rounded" />
      </div>
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <div className="min-h-screen bg-muted text-foreground transition-colors flex flex-col">
        <Navbar />
        <main id="main" className="flex-1">
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              {/* Landing (index and /upload for now) */}
              <Route index element={<UploadModel />} />
              <Route path="/upload" element={<UploadModel />} />

              {/* Public marketing / info */}
              <Route path="/portfolio" element={<Portfolio />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/material-guide" element={<MaterialGuide />} />

              {/* Shop catalog */}
              <Route path="/products" element={<ProductsPage />} />
              <Route path="/products/:slug" element={<ProductDetailPage />} />
              <Route path="/filaments" element={<FilamentsPage />} />
              <Route path="/filaments/:slug" element={<FilamentDetailPage />} />

              {/* Legal pages */}
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/cookies" element={<CookiesPage />} />

              {/* Public auth (redirect authenticated users via PublicAuthRoute) */}
              <Route
                path="/login"
                element={
                  <PublicAuthRoute>
                    <LoginPage />
                  </PublicAuthRoute>
                }
              />
              <Route
                path="/signup"
                element={
                  <PublicAuthRoute>
                    <SignUpPage />
                  </PublicAuthRoute>
                }
              />

              {/* Cart / Checkout */}
              <Route path="/cart" element={<CartPage />} />
              <Route path="/checkout" element={<CheckoutPage />} />

              {/* Account grouped routes */}
              <Route
                path="/account"
                element={
                  <ProtectedRoute>
                    <AccountLayout />
                  </ProtectedRoute>
                }
              >
                <Route path="profile" element={<Profile />} />
                <Route path="orders" element={<OrdersPage />} />
                <Route path="orders/:orderId" element={<OrderDetailPage />} />
                <Route path="company" element={<CompanyPage />} />
                <Route path="settings" element={<SettingsPage />} />
                <Route index element={<Navigate to="/account/profile" replace />} />
              </Route>

              {/* Admin grouped routes */}
              <Route
                path="/admin"
                element={
                  <AdminRoute>
                    <AdminLayout />
                  </AdminRoute>
                }
              >
                <Route path="inventory" element={<InventoryDashboard />} />
                <Route path="clients" element={<AdminClients />} />
                <Route path="orders" element={<AdminOrders />} />
                <Route path="orders/:id" element={<AdminOrderDetail />} />
                <Route path="store-settings" element={<StoreSettings />} />
                <Route path="shipping-methods" element={<ShippingMethods />} />
                <Route path="company-payments" element={<CompanyPayments />} />
                <Route path="catalog/products" element={<AdminCatalogProducts />} />
                <Route path="catalog/filaments" element={<AdminCatalogFilaments />} />
                <Route path="catalog/categories" element={<AdminCatalogCategories />} />
                <Route index element={<Navigate to="/admin/inventory" replace />} />
              </Route>

              {/* Fallback */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </main>
        <Footer />
        <Toaster richColors position="bottom-right" closeButton />
      </div>
    </ThemeProvider>
  );
}

export default App;
// --- END PATCH: Add public /login & /signup routes with PublicAuthRoute, lazy auth pages; keep grouped routes; remove legacy /Home ---